// The main-thread side of offline card search (W5).
//
// Lifecycle (installed PWA and Android app ONLY, see eligibility.ts):
//   - initOfflineCards() runs once after the first render. It reads what is
//     installed and runs the DAILY manifest check: at most once per 24 hours
//     (persisted), triggered by app start, the tab becoming visible and the
//     `online` event. Never a timer, never polling.
//   - A newer manifest version downloads by itself only when
//     navigator.connection says the link is unmetered (wifi or ethernet);
//     anything else waits for the explicit "Download for offline search"
//     tap, which shows the size from the manifest.
//   - The bytes come through apiFetch (the app's fetch boundary); the worker
//     verifies SHA-256 against the manifest version, parses, indexes and
//     stores them in IndexedDB `cb-offline-shared` (not owner data: never
//     fenced, never wiped on sign-out). navigator.storage.persist() is
//     requested, and storage.estimate() is read before the download (C5).
//
// Queries go to the worker. The fallbacks (fallback.ts) ask here whether an
// offline answer is possible at all.
import { useSyncExternalStore } from 'react'
import { apiFetch } from '../../i18n/apiFetch'
import { apiUrl } from '../../api/apiUrl'
import {
  CHECK_INTERVAL_MS, INSTALL_LOCK, MANIFEST_PATH, MSG, OFFLINE_ERROR, STALE_AFTER_MS, STATE,
  UNMETERED_TYPES, type MsgKind, type OfflineErrorCode, type OfflineState,
} from './offlineCards.codes'
import { installedReadable, isOlderThan, manifestSupported } from './installGuard'
import { offlineCardsEligible } from './eligibility'
import { hasIndexedDb, readCheck, readInstalled, writeCheck, type InstalledMeta } from './store'
import type { OfflineSearchAnswer, OfflineSearchRequest, SuggestRow } from './engine'
import type { A2Manifest } from './types'

export interface OfflineCardsStatus {
  eligible: boolean
  state: OfflineState
  installed: InstalledMeta | null
  /** The newest manifest seen (the daily check), null before any check. */
  latest: A2Manifest | null
  /** A manifest newer than what is installed is waiting for a tap. */
  updateAvailable: boolean
  percent: number | null
  error: OfflineErrorCode | null
  checkedAt: string | null
}

/** What answers offline queries: the worker, or a test's in-process engine. */
export interface OfflineBackend {
  search(req: OfflineSearchRequest, offset: number, limit: number): Promise<OfflineSearchAnswer>
  autocomplete(q: string): Promise<string[]>
  suggest(q: string, max: number): Promise<SuggestRow[]>
}

const INITIAL: OfflineCardsStatus = {
  eligible: false, state: STATE.notApplicable, installed: null, latest: null, updateAvailable: false, percent: null, error: null, checkedAt: null,
}
/** A failed manifest check is retried at most this often within one page load. */
const RETRY_AFTER_FAILURE_MS = 60 * 60 * 1000
/** Room asked of the storage estimate, as a multiple of the gzip size (gzip + index + slack). */
const SPACE_FACTOR = 4

let status: OfflineCardsStatus = INITIAL
const listeners = new Set<() => void>()
let initPromise: Promise<void> | null = null
let checking: Promise<void> | null = null
let lastFailedCheck = 0
let downloading: Promise<void> | null = null
let backendOverride: OfflineBackend | null = null

function setStatus(patch: Partial<OfflineCardsStatus>): void {
  status = { ...status, ...patch }
  for (const l of listeners) l()
}

export function getOfflineCardsStatus(): OfflineCardsStatus {
  return status
}

export function subscribeOfflineCards(fn: () => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

export function useOfflineCardsStatus(): OfflineCardsStatus {
  return useSyncExternalStore(subscribeOfflineCards, getOfflineCardsStatus, getOfflineCardsStatus)
}

/**
 * C6 `card_database`: ready ONLY when the stored bytes were confirmed by the
 * worker (`verified`; until then `checking`), the installed version is the
 * latest manifest's and the manifest was checked within 7 days. Stale
 * otherwise (still usable, labelled with its date), including a latest
 * manifest this app cannot read (it needs an app update to move on).
 * Never checked: the install itself read the manifest, so its time stands
 * in for the check.
 */
export function stateFor(
  installed: InstalledMeta | null, latest: A2Manifest | null, checkedAt: string | null, now = Date.now(), verified = true,
): OfflineState {
  if (!installed) return STATE.notStarted
  if (!verified) return STATE.checking
  const checked = checkedAt ? Date.parse(checkedAt) : Date.parse(installed.installed_at)
  if (!Number.isFinite(checked) || now - checked > STALE_AFTER_MS) return STATE.stale
  if (latest && !manifestSupported(latest)) return STATE.stale
  if (latest && latest.version !== installed.version) return STATE.stale
  return STATE.ready
}

/** The newest manifest seen needs a newer app (schema or parser this app cannot read). */
export function latestNeedsAppUpdate(st: OfflineCardsStatus): boolean {
  return !!st.latest && !manifestSupported(st.latest)
}

/** The version whose stored bytes the worker confirmed this page load. */
let verifiedVersion: string | null = null

/** Recompute the state from what is installed and known (not mid-download, not an error). */
function settleState(now = Date.now()): void {
  if (status.state === STATE.downloading || status.state === STATE.error) return
  const verified = !!status.installed && verifiedVersion === status.installed.version
  setStatus({ state: stateFor(status.installed, status.latest, status.checkedAt, now, verified) })
}

/**
 * Ask the worker to confirm the stored bytes (record present, gzip size and
 * SHA-256, index layout) before anything reports ready. A broken install is
 * dropped by the worker and surfaces as error `corrupt` (rpc), with the
 * download offered again.
 */
export async function verifyInstalled(): Promise<void> {
  try {
    const r = await rpc<{ version: string; meta: InstalledMeta } | null>(MSG.verify, {})
    if (!r) {
      verifiedVersion = null
      setStatus({ installed: null, updateAvailable: !!status.latest && supported(status.latest) })
    } else {
      verifiedVersion = r.version
      setStatus({ installed: r.meta })
    }
    settleState()
  } catch (e) {
    if (e instanceof WorkerFailure && e.code === OFFLINE_ERROR.corrupt) return
    setStatus({ state: STATE.error, error: OFFLINE_ERROR.serverError })
  }
}

/** navigator.connection says unmetered (Chrome on Android sets `type`). */
export function isUnmetered(): boolean {
  const c = (navigator as Navigator & { connection?: { type?: string; saveData?: boolean } }).connection
  if (!c || c.saveData) return false
  return !!c.type && UNMETERED_TYPES.includes(c.type)
}

const supported = manifestSupported

async function fetchManifest(): Promise<A2Manifest> {
  // eslint-disable-next-line i18next/no-literal-string -- a fetch cache mode
  const res = await apiFetch(apiUrl(MANIFEST_PATH), { cache: 'no-store' })
  if (!res.ok) throw new Error(OFFLINE_ERROR.serverError)
  return await res.json() as A2Manifest
}

/** The daily manifest check (no-op unless 24 h have passed). */
export function maybeCheckManifest(now: number = Date.now()): Promise<void> {
  if (!status.eligible || checking) return checking ?? Promise.resolve()
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve()
  if (now - lastFailedCheck < RETRY_AFTER_FAILURE_MS) return Promise.resolve()
  checking = (async () => {
    const prev = await readCheck()
    if (prev && now - Date.parse(prev.checked_at) < CHECK_INTERVAL_MS) {
      if (prev.manifest) noteManifest(prev.manifest)
      return
    }
    let manifest: A2Manifest
    try {
      manifest = await fetchManifest()
    } catch {
      lastFailedCheck = now
      return
    }
    const check = await writeCheck(manifest, new Date(now))
    setStatus({ checkedAt: check.checked_at })
    noteManifest(manifest)
  })().finally(() => { checking = null })
  return checking
}

function noteManifest(manifest: A2Manifest): void {
  const newer = supported(manifest) && manifest.version !== status.installed?.version
  setStatus({ latest: manifest, updateAvailable: newer })
  settleState()
  if (newer && isUnmetered() && !downloading) void downloadOfflineCards(manifest)
}

/** Read what is installed and run the daily check. Once per page load. */
export function initOfflineCards(): Promise<void> {
  if (initPromise) return initPromise
  if (!offlineCardsEligible() || !hasIndexedDb() || typeof Worker === 'undefined') {
    initPromise = Promise.resolve()
    return initPromise
  }
  initPromise = (async () => {
    const [stored, check] = await Promise.all([readInstalled(), readCheck()])
    // Metadata this app cannot load (another parser, a half-written record)
    // is not an install: the download is offered again (the worker drops it).
    const installed = installedReadable(stored) ? stored : null
    const latest = check?.manifest ?? null
    setStatus({
      eligible: true,
      installed,
      checkedAt: check?.checked_at ?? null,
      // Never "ready" from metadata alone: checking until the worker confirms.
      state: stateFor(installed, latest, check?.checked_at ?? null, Date.now(), false),
      latest,
      updateAvailable: !!latest && supported(latest) && latest.version !== installed?.version,
    })
    if (installed) void verifyInstalled()
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void maybeCheckManifest()
    })
    window.addEventListener('online', () => { void maybeCheckManifest() })
    await maybeCheckManifest()
  })().catch(() => {
    // IndexedDB refused (private mode, blocked storage): offline search is
    // simply not there, online behavior is untouched.
  })
  return initPromise
}

async function enoughSpace(bytes: number): Promise<boolean> {
  try {
    const est = await navigator.storage?.estimate?.()
    if (!est || typeof est.quota !== 'number') return true
    return est.quota - (est.usage ?? 0) >= bytes * SPACE_FACTOR
  } catch {
    return true
  }
}

async function readWithProgress(res: Response, total: number): Promise<ArrayBuffer> {
  const reader = res.body?.getReader()
  if (!reader) return await res.arrayBuffer()
  const chunks: Uint8Array[] = []
  let got = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    got += value.byteLength
    if (total > 0) setStatus({ percent: Math.min(99, Math.floor((got / total) * 100)) })
  }
  const out = new Uint8Array(got)
  let at = 0
  for (const c of chunks) { out.set(c, at); at += c.byteLength }
  return out.buffer
}

/** Download, verify and install the card data (the tap, or an unmetered auto-download). */
export function downloadOfflineCards(known?: A2Manifest): Promise<void> {
  if (!status.eligible) return Promise.resolve()
  if (downloading) return downloading
  downloading = (async () => {
    setStatus({ state: STATE.downloading, percent: 0, error: null })
    try {
      // A tap reads the manifest again (its version may have moved since the
      // daily check); the last one seen stands in when that read fails.
      let manifest = known ?? null
      if (!manifest) manifest = await fetchManifest().catch(() => status.latest)
      if (!manifest) throw new WorkerFailure(OFFLINE_ERROR.network)
      if (!supported(manifest)) throw new WorkerFailure(OFFLINE_ERROR.unsupported)
      if (!(await enoughSpace(manifest.size))) throw new WorkerFailure(OFFLINE_ERROR.storageFull)
      void navigator.storage?.persist?.().catch(() => false)
      const target = manifest
      // ONE install at a time across every window of the installed app
      // (Web Locks are cross-context). Inside the lock, what is installed is
      // read again: another window may have installed this version or a
      // newer one meanwhile, and an older version never replaces a newer one.
      let kept = false
      const installed = await withInstallLock(async () => {
        const current = await readInstalled()
        if (installedReadable(current) && (current.version === target.version || isOlderThan(target, current.manifest))) {
          kept = true
          return current
        }
        const fetched = await fetchArtifact(target)
        const result = await rpc<{ meta: InstalledMeta; kept?: boolean }>(MSG.install, { bytes: fetched, manifest: target }, [fetched])
        kept = result.kept === true
        return result.meta
      })
      // Just built and stored by this worker: confirmed. Kept (another
      // window's install): confirmed by verifyInstalled below.
      verifiedVersion = kept ? verifiedVersion : installed.version
      const latest = status.latest && isOlderThan(target, status.latest) ? status.latest : target
      setStatus({ state: STATE.ready, installed, percent: null, error: null, latest, updateAvailable: supported(latest) && latest.version !== installed.version })
      settleState()
      if (kept && verifiedVersion !== installed.version) void verifyInstalled()
    } catch (e) {
      const code = e instanceof WorkerFailure ? e.code : OFFLINE_ERROR.network
      setStatus({ state: STATE.error, percent: null, error: code as OfflineErrorCode })
    }
  })().finally(() => { downloading = null })
  return downloading
}

async function fetchArtifact(manifest: A2Manifest): Promise<ArrayBuffer> {
  let res: Response
  try {
    res = await apiFetch(apiUrl(manifest.url))
  } catch {
    throw new WorkerFailure(OFFLINE_ERROR.network)
  }
  if (!res.ok) throw new WorkerFailure(OFFLINE_ERROR.serverError)
  return await readWithProgress(res, manifest.size)
}

interface LockManagerLike { request<T>(name: string, cb: () => Promise<T>): Promise<T> }

/** Run `fn` holding the cross-window install lock (in-process only when Web Locks are missing). */
export function withInstallLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: LockManagerLike }).locks : undefined
  return locks?.request ? locks.request(INSTALL_LOCK, fn) : fn()
}

// --- the worker ---

class WorkerFailure extends Error {
  readonly code: string
  constructor(code: string) {
    super(code)
    this.code = code
  }
}

let worker: Worker | null = null
let nextId = 1
const pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>()

/** A dead worker: terminate it, reject every outstanding call, start fresh next time. */
function dropWorker(w: Worker | null): void {
  if (w && worker !== w) return
  try { w?.terminate() } catch { /* already gone */ }
  if (worker === w) worker = null
  for (const p of pending.values()) p.reject(new WorkerFailure(OFFLINE_ERROR.serverError))
  pending.clear()
}

function getWorker(): Worker {
  if (worker) return worker
  // eslint-disable-next-line i18next/no-literal-string -- a worker type
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' })
  worker.onmessage = (ev: MessageEvent<{ id: number; ok: boolean; result?: unknown; error?: string }>) => {
    const p = pending.get(ev.data.id)
    if (!p) return
    pending.delete(ev.data.id)
    if (ev.data.ok) p.resolve(ev.data.result)
    else p.reject(new WorkerFailure(ev.data.error ?? OFFLINE_ERROR.serverError))
  }
  const w = worker
  w.onerror = () => dropWorker(w)
  w.onmessageerror = () => dropWorker(w)
  return w
}

function rpc<T>(kind: MsgKind, payload: Record<string, unknown>, transfer: Transferable[] = []): Promise<T> {
  const w = getWorker()
  const id = nextId++
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
    try {
      w.postMessage({ id, kind, ...payload }, transfer)
    } catch (e) {
      pending.delete(id)
      reject(e)
    }
  }).catch((e: unknown) => {
    // The worker found the installed data broken and dropped it: offline
    // search stops answering and the download is offered again.
    if (e instanceof WorkerFailure && e.code === OFFLINE_ERROR.corrupt) {
      setStatus({ installed: null, state: STATE.error, error: OFFLINE_ERROR.corrupt, updateAvailable: !!status.latest })
    }
    throw e
  })
}

// The worker validates and (re)loads the installed version on every query.
const workerBackend: OfflineBackend = {
  search(req, offset, limit) { return rpc(MSG.search, { req, offset, limit }) },
  autocomplete(q) { return rpc(MSG.autocomplete, { q }) },
  suggest(q, max) { return rpc(MSG.suggest, { q, max }) },
}

/** Card data is installed in this installed app and can answer queries. */
export function offlineSearchAvailable(): boolean {
  if (backendOverride) return true
  return status.eligible && status.installed !== null
}

export function offlineBackend(): OfflineBackend {
  return backendOverride ?? workerBackend
}

/** Test seam: answer offline queries in-process (null restores the worker). */
export function __setOfflineBackendForTests(b: OfflineBackend | null): void {
  backendOverride = b
}

/** Test seam: a fresh module state. */
export function __resetOfflineCardsForTests(patch: Partial<OfflineCardsStatus> = {}): void {
  status = { ...INITIAL, ...patch }
  initPromise = null
  checking = null
  downloading = null
  lastFailedCheck = 0
  backendOverride = null
  verifiedVersion = null
  dropWorker(worker)
  for (const l of listeners) l()
}
